import { useContext } from 'react';
import { getVideoById, Video } from '../../utils/api/videoApi';
import { Color, colors } from '../../utils/styleguide';
import HeadlinerLayout from '../component-library/layout/HeadlinerLayout';
import HeadlinerVideoLayout from '../component-library/layout/HeadlinerVideoLayout';
import { TemplatingContext } from './contexts/TemplatingContext';
import { PageSectionComponent } from './pageSections';
import RichText from './RichText/RichText';
import sectionToCompatibleProps from './util/sectionToCompatibleProps';
import sectionToCompatibleServerSideProps from './util/sectionToCompatibleServerSideProps';

type ServerSideProps = {
  portraitVideo?: Video;
  landscapeVideo?: Video;
  portraitBackgroundVideo?: Video;
  landscapeBackgroundVideo?: Video;
};

const HeadlinerSection: PageSectionComponent<ServerSideProps> = ({
  section,
  serverSideProps: {
    portraitVideo,
    landscapeVideo,
    portraitBackgroundVideo,
    landscapeBackgroundVideo,
  },
}) => {
  const { fields: { settings, image: deprecatedImage } = {} } = section;
  const {
    id,
    title,
    description,
    button,
    backgroundColor,
    backgroundLinear,
    backgroundImage,
  } = sectionToCompatibleProps({ section });

  const deprecatedTitleColor = colors[settings?.titleColor as Color];
  const deprecatedDescriptionColor = colors[settings?.descriptionColor as Color];

  const { templateReplacer } = useContext(TemplatingContext);
  let href = button.href;
  if (href) {
    href = templateReplacer(href);
  }

  if (!portraitBackgroundVideo || !landscapeBackgroundVideo) {
    return (
      <HeadlinerLayout
        id={id}
        title={<RichText document={title} textColor={deprecatedTitleColor} centered />}
        description={
          description && (
            <RichText document={description} textColor={deprecatedDescriptionColor} centered />
          )
        }
        image={backgroundImage || deprecatedImage}
        CTAText={button.label}
        CTAPrompt={button.prompt}
        CTAColor={button.colorContext}
        buttonSmall={button.size === 'small'}
        portraitVideo={portraitVideo}
        landscapeVideo={landscapeVideo}
        href={href}
        backgroundColor={backgroundColor}
        backgroundLinear={backgroundLinear}
        small={settings?.small}
      />
    );
  }

  return (
    <HeadlinerVideoLayout
      title={<RichText document={title} textColor={deprecatedTitleColor} centered />}
      description={
        description && (
          <RichText document={description} textColor={deprecatedDescriptionColor} centered />
        )
      }
      CTAText={button.label}
      CTAPrompt={button.prompt}
      portraitVideo={portraitVideo}
      landscapeVideo={landscapeVideo}
      portraitBackgroundVideo={portraitBackgroundVideo}
      landscapeBackgroundVideo={landscapeBackgroundVideo}
      href={href}
    />
  );
};

HeadlinerSection.getCustomServerSideProps = async ({ section }) => {
  const {
    backgroundVideoIdLandscape,
    backgroundVideoIdPortrait,
    videoIdLandscape,
    videoIdPortrait,
  } = sectionToCompatibleServerSideProps({ section });
  const [
    portraitVideo,
    landscapeVideo,
    portraitBackgroundVideo,
    landscapeBackgroundVideo,
  ] = await Promise.all([
    getVideoById(videoIdPortrait),
    getVideoById(videoIdLandscape),
    getVideoById(backgroundVideoIdPortrait),
    getVideoById(backgroundVideoIdLandscape),
  ]);
  return { portraitVideo, landscapeVideo, portraitBackgroundVideo, landscapeBackgroundVideo };
};

export default HeadlinerSection;
